// Where a line comes from, on every table (`deposco.ts`): the Source filter (All / A2000 / Deposco),
// kept per person in this browser and shared by every page; the small badge on each line, row and
// log entry; and the Deposco orders that are not lines yet (a DC, no group), counted and listed.

import { useState } from "react";
import {
  SOURCE_FILTERS,
  countText,
  deposcoShippedText,
  deposcoStatusText,
  formatShortDay,
  isSourceFilter,
  lineRef,
  ptText,
  ptWord,
  shownCustomerText,
} from "@app/shared";
import type { CustomerNamer, DeposcoBrief, LineSource, LineView, SourceFilter } from "@app/shared";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { useDialogDrag } from "./dialog-drag";

const KEY = "routing.source";

function read(): SourceFilter {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(KEY) ?? '"all"');
    return isSourceFilter(v) ? v : "all";
  } catch {
    return "all";
  }
}

/** The person's Source filter: one choice for every table, kept in this browser. */
export function useSourceFilter(): [SourceFilter, (s: SourceFilter) => void] {
  const [source, setSource] = useState<SourceFilter>(read);
  return [
    source,
    (s) => {
      setSource(s);
      try {
        localStorage.setItem(KEY, JSON.stringify(s));
      } catch {
        // Private window: kept for this page only.
      }
    },
  ];
}

/** "Source: All · A2000 · Deposco", each with its count of what the page's other filters leave. */
export function SourcePicker({
  value,
  counts,
  onChange,
}: {
  value: SourceFilter;
  counts?: Partial<Record<LineSource, number>>;
  onChange: (s: SourceFilter) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Source"
      className="bg-muted inline-flex h-8 shrink-0 items-center gap-0.5 rounded-md p-0.5 text-xs"
      data-testid="source-filter"
    >
      <span className="text-muted-foreground px-1.5 font-medium">Source</span>
      {SOURCE_FILTERS.map((s) => {
        const n = s.id === "all" ? undefined : counts?.[s.id];
        return (
          <button
            key={s.id}
            type="button"
            role="radio"
            aria-checked={value === s.id}
            onClick={() => onChange(s.id)}
            className={cn(
              "flex h-7 items-center gap-1 rounded px-2 whitespace-nowrap",
              value === s.id ? "bg-background text-foreground font-semibold shadow-sm" : "text-muted-foreground",
            )}
          >
            {s.label}
            {n !== undefined ? <span className="tabular-nums opacity-70">{n.toLocaleString("en-US")}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

const TONE: Record<LineSource, string> = {
  A2000: "bg-slate-100 text-slate-700 ring-slate-300",
  Deposco: "bg-sky-50 text-sky-800 ring-sky-300",
};

/** The small "A2000" / "Deposco" badge of a line, a row or a log entry. */
export function SourceBadge({ source, className }: { source: LineSource; className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-5 shrink-0 items-center rounded px-1.5 text-[10px] font-semibold ring-1 ring-inset",
        TONE[source],
        className,
      )}
      data-testid="source-badge"
    >
      {source}
    </span>
  );
}

/** An SO's customer as a Deposco line shows it (`customerNamer`): its shown name, the original on hover. */
const customerOf = (r: DeposcoBrief, name: CustomerNamer) =>
  name({ code: r.customer_code ?? "", name: r.customer_name ?? "", source: "Deposco" });

/**
 * The open Deposco SOs waiting for a group (a DC, no group) — no line yet, New ones too — counted, the
 * list on click, so they stay in sight. Every other open SO is on a line.
 */
export function DeposcoNotLines({ rows, name }: { rows: readonly DeposcoBrief[]; name: CustomerNamer }) {
  const drag = useDialogDrag();
  const [open, setOpen] = useState(false);
  if (!rows.length) return null;
  const sorted = [...rows].sort(
    (a, b) =>
      String(a.customer_name ?? "").localeCompare(String(b.customer_name ?? "")) ||
      String(a.customer_po ?? "").localeCompare(String(b.customer_po ?? "")) ||
      a.so_number.localeCompare(b.so_number),
  );
  return (
    <>
      <button
        type="button"
        className="text-primary py-1 hover:underline"
        onClick={() => {
          drag.reset();
          setOpen(true);
        }}
        data-testid="deposco-waiting"
      >
        {`${countText(rows.length, "Deposco order")} waiting for a group`}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent ref={drag.ref} style={drag.style} initialFocus={drag.ref} className="sm:max-w-3xl">
          <DialogHeader {...drag.handle}>
            <DialogTitle>Deposco orders waiting for a group</DialogTitle>
            <DialogDescription>
              They are open and have a DC but no group in Deposco, so they are not on the Worklist yet. Each joins its
              line (customer + PO + DC) once Deposco gives it a group.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-md border">
            <table className="w-full text-[13px]">
              <thead className="bg-locked sticky top-0 text-left text-xs">
                <tr className="[&>th]:px-2 [&>th]:py-1.5 [&>th]:font-medium">
                  <th>SO #</th>
                  <th>Customer</th>
                  <th>PO</th>
                  <th>DC</th>
                  <th>Deposco status</th>
                  <th>Received</th>
                  <th>Cancel</th>
                  <th className="text-right">Qty</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.so_number} className="border-t [&>td]:px-2 [&>td]:py-1">
                    <td className="font-mono">{r.so_number}</td>
                    <td className="max-w-48 truncate" title={customerOf(r, name).full}>
                      {customerOf(r, name).text}
                    </td>
                    <td>{r.customer_po}</td>
                    <td>{r.dc_no}</td>
                    <td>{r.deposco_status}</td>
                    <td>{r.received_date ? formatShortDay(r.received_date) : "—"}</td>
                    <td>{r.cancel_date ? formatShortDay(r.cancel_date) : "—"}</td>
                    <td className="text-right tabular-nums">{(r.qty ?? 0).toLocaleString("en-US")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A line's source, and on a Deposco line its SOs, each with its Deposco status (the one behind
 * first: MIXED is fixed in Deposco), its group, DC, DLIX and CO, and what Deposco shipped. Line
 * details, the shipment sheet and the Shipping dialogs show it.
 */
export function LineSourceFacts({ line }: { line: LineView }) {
  const d = line.deposco;
  if (!d)
    return (
      <p className="flex items-center gap-1.5 text-xs">
        <SourceBadge source={line.source} />
        <span className="text-muted-foreground">{line.wmsNo ? `One WMS # (${line.wmsNo})` : "A2000"}</span>
      </p>
    );
  const sos = d.status?.sos ?? [];
  const byPt = new Map(line.pts.map((p) => [p.pickTicket, p]));
  return (
    <section className="space-y-1.5" data-testid="deposco-sos">
      <p className="flex flex-wrap items-center gap-1.5 text-xs">
        <SourceBadge source="Deposco" />
        <span className="text-muted-foreground">
          {[
            `customer + PO${line.dcs.length ? " + DC" : ""}`,
            d.status ? `status ${deposcoStatusText(d.status)}` : "",
            d.groups.length ? `group ${d.groups.join(", ")}` : "",
            d.dlixes.length ? `DLIX ${d.dlixes.join(", ")}` : "",
            d.cos.length ? `CO ${d.cos.join(", ")}` : "",
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </p>
      {d.shipped ? <p className="text-xs font-medium text-teal-800">{deposcoShippedText(d.shipped)}</p> : null}
      <table className="text-xs">
        <thead className="text-muted-foreground text-left">
          <tr className="[&>th]:pr-3 [&>th]:font-medium">
            <th>SO #</th>
            <th>Deposco status</th>
            <th>DC</th>
            <th className="text-right">Qty</th>
          </tr>
        </thead>
        <tbody>
          {sos.map((x) => {
            const p = byPt.get(x.so);
            return (
              <tr key={x.so} className={cn("[&>td]:pr-3", p?.gone && "text-muted-foreground line-through")}>
                <td className="font-mono">{x.so}</td>
                <td>
                  {x.status || "—"}
                  {d.status?.mixed && x.status === sos[0]?.status ? (
                    <span className="ml-1 text-[10px] font-semibold text-violet-700">behind</span>
                  ) : null}
                </td>
                <td>{p?.dc || "—"}</td>
                <td className="text-right tabular-nums">{(p?.qty ?? 0).toLocaleString("en-US")}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

/** The lines a Shipping dialog acts on: each one's source, how it is known, and a Deposco line's SOs with their statuses. */
export function DialogLines({ lines }: { lines: readonly LineView[] }) {
  const SHOWN = 4;
  return (
    <ul className="max-h-36 space-y-1 overflow-y-auto text-xs" data-testid="dialog-lines">
      {lines.map((l) => {
        const sos = l.deposco?.status?.sos ?? [];
        return (
          <li key={l.key} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
            <SourceBadge source={l.source} />
            <span className="font-medium">{shownCustomerText(l) || "—"}</span>
            <span className="text-muted-foreground">
              {[lineRef(l), sos.length ? "" : `${ptWord(l)} ${ptText(l, 3)}`].filter(Boolean).join(" · ")}
            </span>
            {sos.length ? (
              <span className="text-muted-foreground basis-full pl-1">
                {sos
                  .slice(0, SHOWN)
                  .map((x) => `${x.so} ${x.status || "—"}`)
                  .join(" · ")}
                {sos.length > SHOWN ? ` · +${sos.length - SHOWN} more SOs` : ""}
                {l.deposco?.status?.mixed ? " · MIXED" : ""}
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
